import { createContext, useContext } from "react";
import { Backend } from "../api/backend";
import { ApiClient, LocalCredentialStore } from "../api/http";
import { AppleMusic } from "../music/appleMusic";
import { appleTrackResolver } from "../music/resolveTrack";
import { SoundCloudOutput, VoiceOutput } from "../playback/audioOutput";
import type { TrackResolver } from "../playback/blocks";
import { PlaybackEngine } from "../playback/engine";
import { MusicKitOutput } from "../playback/musicKitOutput";
import { Station } from "../radio/station";

export const api = new ApiClient(new LocalCredentialStore());
export const backend = new Backend(api);

/** What exists once Apple Music is loaded: the player and the station on top of it. */
export interface Player {
  music: AppleMusic;
  engine: PlaybackEngine;
  station: Station;
  /** Finds a segment's recording in this listener's storefront. */
  resolveTrack: TrackResolver;
}

export async function makePlayer(): Promise<Player> {
  const music = await AppleMusic.load(backend);
  const engine = new PlaybackEngine({
    voice: new VoiceOutput(),
    appleMusic: new MusicKitOutput(music.kit),
    soundCloud: new SoundCloudOutput(backend),
  });
  const resolveTrack = appleTrackResolver(music);
  const station = new Station(backend, engine, music, resolveTrack);
  return { music, engine, station, resolveTrack };
}

export const PlayerContext = createContext<Player | null>(null);

export function usePlayer(): Player {
  const player = useContext(PlayerContext);
  if (!player) throw new Error("usePlayer outside PlayerContext");
  return player;
}
